import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Feature } from 'app/api/models';
import { EmployeeService, SettingsService } from 'app/api/services';
import { NavigationAccess, filterNavigation } from 'app/core/services/navigation/navigation-access';
import { Navigation } from 'app/core/services/navigation/navigation.types';
import { PermissionService } from 'app/core/services/permissions/permission.service';
import { Observable, ReplaySubject, catchError, forkJoin, map, of, switchMap, tap } from 'rxjs';

/**
 * The sidebar, already cut down to what the signed-in person may reach.
 *
 * The menu itself is static (mock-api/common/navigation/data.ts). What somebody sees of it is:
 *   - the modules switched on for this installation (Settings > Features), and
 *   - the permissions they hold (navigation-access.ts says which item needs which).
 *
 * It is filtered HERE, once, for every layout, rather than inside a layout or the Fuse component,
 * and it is filtered AGAIN whenever their permissions change, so a change of role reshapes the menu
 * without a reload.
 *
 * Cosmetic, like all frontend gating: routes carry PermissionGuard and endpoints carry
 * [Authorize(Permissions.X)], which is the actual boundary.
 */
@Injectable({providedIn: 'root'})
export class NavigationService
{
    private _navigation: ReplaySubject<Navigation> = new ReplaySubject<Navigation>(1);

    private _permissions = inject(PermissionService);
    private _settingsService = inject(SettingsService);
    private _employeeService = inject(EmployeeService);

    // What the last get() fetched, kept so the menu can be re-cut without fetching it all again.
    private _unfiltered: Navigation | null = null;
    private _enabledFeatures: Feature[] = [];
    private _canClockInOut = false;

    /**
     * Constructor
     */
    constructor(private _httpClient: HttpClient)
    {
        // Emits only on a real change. Anything before the first get() has nothing to re-cut yet.
        this._permissions.codes$.subscribe(() =>
        {
            if ( this._unfiltered ) this._navigation.next(this._filter(this._unfiltered));
        });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for navigation
     */
    get navigation$(): Observable<Navigation>
    {
        return this._navigation.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get all navigation data
     */
    get(): Observable<Navigation>
    {
        return this._permissions.load().pipe(
            switchMap((me) => forkJoin({
                navigation: this._httpClient.get<Navigation>('api/common/navigation'),

                // Failing to find out what is switched on hides those modules rather than showing them.
                features: this._settingsService.apiSettingsAllowedFeaturesGet$Json().pipe(
                    map((res) => res?.data ?? []),
                    catchError(() => of([] as Feature[])),
                ),

                canClockInOut: !me?.employeeId ? of(false) :
                    this._employeeService.apiEmployeeEmployeeCanClockInOutGet$Json({ userId: me.employeeId }).pipe(
                        map((res) => !!res?.data),
                        catchError(() => of(false)),
                    ),
            })),
            map(({ navigation, features, canClockInOut }) =>
            {
                this._unfiltered = navigation;
                this._enabledFeatures = features;
                this._canClockInOut = canClockInOut;

                return this._filter(navigation);
            }),
            tap((navigation) =>
            {
                this._navigation.next(navigation);
            }),
        );
    }

    private _filter(navigation: Navigation): Navigation
    {
        const access: NavigationAccess = {
            codes: new Set(this._permissions.codes),
            enabledFeatures: this._enabledFeatures,
            canClockInOut: this._canClockInOut,
        };

        return {
            ...navigation,
            default: filterNavigation(navigation.default, access),
            compact: filterNavigation(navigation.compact, access),
            futuristic: filterNavigation(navigation.futuristic, access),
            horizontal: filterNavigation(navigation.horizontal, access),
        };
    }
}
